<script setup>
import TypewriterText from "../ui/TypewriterText.vue";
import LeadForm from "../ui/LeadForm.vue";
import BaseButton from "../ui/BaseButton.vue";
import IconCheck from "../icons/IconCheck.vue";
import IconArrow from "../icons/IconArrow.vue";
import HeroPhones from "./HeroPhones.vue";
import MagneticWrap from "../ui/MagneticWrap.vue";
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useMediaQuery } from "../../composables/useMediaQuery";
import { usePauseOffscreen } from "../../composables/usePauseOffscreen";
import { prefersReducedMotion } from "../../composables/useScrollReveal";
import {
  scrollToSection,
  scrollToLeadForm,
} from "../../composables/useScrollTo";
import { useLanding } from "../../composables/useLanding";
import { useGsap } from "../../composables/useGsap";
import { useMouseInElement } from "@vueuse/core";

/*
 * On phones the hero carries a button rather than the form itself — the form
 * lives once, at the bottom of the page. Rendering conditionally rather than
 * hiding with CSS keeps a second set of inputs out of the DOM, where autofill
 * and screen readers would still find them.
 */
const isDesktop = useMediaQuery("(min-width: 900px)");

const hero = useLanding().hero;

// The schedule's hour lines. The slot spans the middle two hours.
const hours = ["08:00", "09:00", "10:00", "11:00"];

/*
 * The slot is live: on the studio page its seats fill one by one until the
 * class reads full, on the salon page the chair flips from open to booked.
 * One timer drives both, and it stops while the hero is off screen.
 */
const board = ref(null);
const { paused } = usePauseOffscreen(board);

const seats = hero.slot.seats;
const status = hero.slot.status;
const firstSeats = 3;
const taken = ref(seats ? seats.of : 0);
const booked = ref(true);
const isFull = computed(() => seats && taken.value === seats.of);

let timer = null;
let tick = 0;

function step() {
  tick += 1;
  if (seats) {
    // Fill, hold on full for a few beats, then start the next day over.
    if (taken.value < seats.of) taken.value += 1;
    else if (tick % 6 === 0) taken.value = firstSeats;
  } else if (status && tick % 4 === 0) {
    booked.value = !booked.value;
  }
}

function start() {
  if (timer || prefersReducedMotion()) return;
  timer = setInterval(step, 700);
}

function stop() {
  clearInterval(timer);
  timer = null;
}

onMounted(() => {
  if (prefersReducedMotion()) return;
  taken.value = firstSeats;
  booked.value = false;
  start();
});

watch(paused, (isPaused) => (isPaused ? stop() : start()));
onBeforeUnmount(stop);

/*
 * The devices lean a few degrees towards the pointer. Desktop pointers only;
 * the values go to CSS variables, so a frame costs one style write.
 */
const devices = ref(null);
const tilt = ref({ x: 0, y: 0 });
const canTilt = useMediaQuery("(hover: hover) and (pointer: fine)");
let frame = null;

function onPointerMove(event) {
  if (!canTilt.value || prefersReducedMotion() || frame) return;
  frame = requestAnimationFrame(() => {
    frame = null;
    const rect = devices.value.getBoundingClientRect();
    tilt.value = {
      x: ((event.clientX - rect.left) / rect.width - 0.5) * 2,
      y: ((event.clientY - rect.top) / rect.height - 0.5) * 2,
    };
  });
}

function onPointerLeave() {
  tilt.value = { x: 0, y: 0 };
}

/*
 * The grid behind the hero lights up around the pointer. VueUse tracks the
 * position; CSS draws the light as a mask on a second, brighter grid.
 */
const heroEl = ref(null);
const { elementX, elementY, isOutside } = useMouseInElement(heroEl);
const gridLight = computed(() => ({
  "--hx": `${elementX.value}px`,
  "--hy": `${elementY.value}px`,
}));

/*
 * The schedule assembles itself on load: the hour rules draw in, the slot
 * rises onto them, then the devices land on the slot. Scrolling away, the
 * visual drifts up faster than the copy, so the hero has some depth.
 */
useGsap(heroEl, (gsap) => {
  gsap
    .timeline({ defaults: { ease: "expo.out" }, delay: 0.25 })
    .from(".slot-board__hours li", {
      clipPath: "inset(0 0 0 100%)",
      duration: 1.1,
      stagger: 0.12,
    })
    .from(
      ".slot-board__slot",
      { scaleY: 0.4, opacity: 0, transformOrigin: "50% 100%", duration: 0.9 },
      "-=0.75"
    )
    .from(
      ".slot-board__devices > *",
      { y: 70, opacity: 0, duration: 1.2 },
      "-=0.6"
    )
    .from(".hero__orbit", { opacity: 0, scale: 0.92, duration: 1.6 }, 0);

  if (!isDesktop.value) return;
  const scrub = { trigger: heroEl.value, start: "top top", end: "bottom top", scrub: 0.6 };
  gsap.to(".hero__visual", { yPercent: -12, ease: "none", scrollTrigger: scrub });
  gsap.to(".hero__copy", { yPercent: 6, ease: "none", scrollTrigger: { ...scrub } });
});
</script>

<template>
  <section
    ref="heroEl"
    class="hero"
    :class="{ 'is-lit': !isOutside }"
    :style="gridLight"
    id="hero"
  >
    <!-- Inspira UI grid patterns: a faint one, and a brighter copy that only
         shows in a circle around the pointer. -->
    <div class="hero__grid bg-grid-small-grid-faint" aria-hidden="true" />
    <div class="hero__grid hero__grid--lit bg-grid-small-grid-lit" aria-hidden="true" />

    <!-- A ring around the schedule, with a dot travelling along it. -->
    <div class="hero__orbit" :class="{ 'is-paused': paused }" aria-hidden="true">
      <i />
    </div>

    <div class="hero__inner container">
      <div class="hero__copy">
        <span class="eyebrow">
          <IconCheck />
          {{ hero.eyebrow }}
        </span>

        <h1 class="visually-hidden">{{ hero.slogan.join(". ") }}</h1>
        <TypewriterText :lines="hero.slogan" />

        <p class="hero__lead lead">{{ hero.lead }}</p>

        <ul class="hero__proof" role="list">
          <li
            v-for="(item, i) in hero.proof"
            :key="item"
            class="hero__proof-item animate-in fade-in slide-in-from-bottom-3 fill-mode-both duration-700"
            :style="{ animationDelay: `${900 + i * 120}ms` }"
          >
            <IconCheck />
            <span>{{ item }}</span>
          </li>
        </ul>

        <div
          v-if="isDesktop"
          class="hero__form-card spotlight animate-in fade-in slide-in-from-bottom-6 fill-mode-both duration-1000 delay-700"
        >
          <h2 class="hero__form-title">רוצים לראות איך זה עובד?</h2>
          <p class="hero__form-text">
            השאירו שם וטלפון, ונחזור אליכם עם הדגמה קצרה.
          </p>
          <LeadForm
            variant="hero"
            form-id="hero"
            id-prefix="hero"
            submit-label="קבלו הדגמה"
          />
        </div>

        <MagneticWrap v-else class="hero__cta-wrap">
          <BaseButton
            size="lg"
            class="hero__cta"
            @click="scrollToLeadForm('hero_mobile')"
          >
            קבעו הדגמה
          </BaseButton>
        </MagneticWrap>

        <BaseButton
          variant="ghost"
          size="sm"
          class="hero__scroll"
          @click="scrollToSection('features')"
        >
          <template #icon><IconArrow /></template>
          מה יש באפליקציה
        </BaseButton>
      </div>

      <!--
        The phones stand on a booked slot in a day's schedule: hour lines, and
        one session drawn the way the app draws it. Decoration; the copy
        beside it says what the product does.
      -->
      <div class="hero__visual">
        <div
          ref="board"
          class="slot-board"
          :class="{ 'is-paused': paused }"
          @pointermove="onPointerMove"
          @pointerleave="onPointerLeave"
        >
          <ol class="slot-board__hours" role="list" aria-hidden="true">
            <li v-for="hour in hours" :key="hour">{{ hour }}</li>
          </ol>

          <!-- The current time, drifting down the morning. -->
          <span class="slot-board__now" aria-hidden="true"><i /></span>

          <div class="slot-board__slot" aria-hidden="true">
            <span class="slot-board__time">{{ hero.slot.time }}</span>
            <span class="slot-board__title">{{ hero.slot.title }}</span>

            <span v-if="seats" class="slot-board__seats">
              <span class="seat-row">
                <span
                  v-for="(initial, n) in seats.initials.slice(0, seats.of)"
                  :key="n"
                  class="seat"
                  :class="{ 'is-taken': n < taken }"
                >{{ initial }}</span>
              </span>
              <span class="seat-count" :class="{ 'is-full': isFull }">
                <template v-if="isFull">{{ hero.slot.fullLabel }}</template>
                <template v-else>{{ taken }}/{{ seats.of }}</template>
              </span>
            </span>

            <span
              v-else-if="status"
              class="slot-board__status"
              :class="{ 'is-booked': booked }"
            >
              <IconCheck v-if="booked" />
              {{ booked ? status.booked : status.open }}
            </span>
          </div>

          <div
            ref="devices"
            class="slot-board__devices"
            :style="{ '--tilt-x': tilt.x, '--tilt-y': tilt.y }"
          >
            <HeroPhones
              v-if="hero.screens"
              :screens="hero.screens"
              :notifications="hero.notifications"
            />
            <img
              v-else
              class="hero__device"
              :src="hero.image"
              :alt="hero.imageAlt"
              width="560"
              height="640"
              fetchpriority="high"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
/*
 * A light behind the devices, a warmer one under the copy, and the page
 * settling into the brand tint at the bottom. Two quiet shapes on top:
 * a thin ring around the schedule and a patch of dots by the headline.
 * Static, and gradients only — no filters (see motion.css).
 */
.hero {
  position: relative;
  overflow: hidden;
  background: radial-gradient(
      55% 65% at 8% 40%,
      var(--wash-brand),
      transparent 70%
    ),
    radial-gradient(40% 45% at 100% 100%, var(--wash-accent), transparent 70%),
    linear-gradient(180deg, var(--surface) 40%, var(--brand-softer));
  padding-block: var(--space-8) var(--section-gap);
}

/*
 * The Inspira grid. The faint layer fades out from the copy's corner; the lit
 * layer is masked to a circle at the pointer (--hx / --hy, set from VueUse),
 * and only shows while the pointer is over the hero.
 */
.hero__grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  -webkit-mask-image: radial-gradient(
    70% 70% at 85% 20%,
    black,
    transparent 75%
  );
  mask-image: radial-gradient(70% 70% at 85% 20%, black, transparent 75%);
}

.hero__grid--lit {
  opacity: 0;
  transition: opacity var(--dur-slow) var(--ease-out);
  -webkit-mask-image: radial-gradient(
    260px circle at var(--hx) var(--hy),
    black,
    transparent 70%
  );
  mask-image: radial-gradient(
    260px circle at var(--hx) var(--hy),
    black,
    transparent 70%
  );
}

@media (hover: hover) and (pointer: fine) {
  .hero.is-lit .hero__grid--lit {
    opacity: 1;
  }
}

/*
 * A thin ring around the schedule, and a point of light orbiting it once a
 * minute. The ring sits still; only the inner wrapper turns.
 */
.hero__orbit {
  position: absolute;
  inset-inline-end: -12vw;
  inset-block-start: 4%;
  inline-size: min(62vw, 760px);
  aspect-ratio: 1;
  border: 1px solid var(--wash-line);
  border-radius: 50%;
  pointer-events: none;
}

.hero__orbit i {
  position: absolute;
  inset: -1px;
  border-radius: 50%;
  animation: orbit 60s linear infinite;
}

.hero__orbit i::before {
  content: "";
  position: absolute;
  inset-block-start: 50%;
  inset-inline-start: -5px;
  inline-size: 9px;
  block-size: 9px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 5px rgba(255, 122, 69, 0.18);
}

@keyframes orbit {
  to {
    transform: rotate(360deg);
  }
}

/* ----------------------------------------------------------------- layout */

.hero__inner {
  position: relative;
  z-index: var(--z-base);
  display: grid;
  gap: var(--space-8);
  align-items: center;
}

.hero__copy {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--space-5);
}

.hero__lead {
  max-inline-size: 52ch;
  margin: 0;
}

/* ------------------------------------------------------------------ proof */

.hero__proof {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2) var(--space-5);
  list-style: none;
  padding: 0;
  margin: 0;
}

.hero__proof-item {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  color: var(--ink-muted);
}

.hero__proof-item :deep(svg) {
  inline-size: 18px;
  block-size: 18px;
  color: var(--success);
  stroke-width: 2.4;
  flex-shrink: 0;
}

/* ------------------------------------------------------------- form card */

.hero__form-card {
  position: relative;
  inline-size: 100%;
  max-inline-size: 460px;
  padding: var(--space-5);
  background: var(--surface);
  border: var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--lift-2);
}

.hero__form-title {
  font-family: var(--font-sans);
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
  line-height: var(--leading-snug);
  margin-block-end: var(--space-1);
}

.hero__form-text {
  font-size: var(--text-sm);
  color: var(--ink-muted);
  margin-block-end: var(--space-4);
}

.hero__cta-wrap {
  inline-size: 100%;
  max-inline-size: 320px;
}

.hero__cta {
  inline-size: 100%;
}

.hero__scroll {
  margin-inline-start: calc(var(--space-4) * -1);
}

/* ----------------------------------------------------------------- visual */

.hero__visual {
  display: flex;
  justify-content: center;
}

/*
 * The schedule: four hour lines with their times at the reading edge, and
 * the slot between the second and the fourth. The devices sit on top and
 * overhang the slot, so the block reads as the stage they stand on.
 */
.slot-board {
  --label-col: 3.25rem;
  position: relative;
  inline-size: min(100%, 560px);
  /* The room at the bottom keeps the devices clear of the slot's label. */
  padding-block: var(--space-6) calc(var(--space-10) + var(--space-6));
  padding-inline-start: var(--label-col);
}

.slot-board__hours {
  position: absolute;
  inset: 0;
  display: grid;
  grid-template-rows: repeat(3, 1fr) 0;
  list-style: none;
  margin: 0;
  padding: 0;
}

.slot-board__hours li {
  border-block-start: 1px solid var(--line);
  padding-block-start: var(--space-1);
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  font-variant-numeric: tabular-nums;
  color: var(--ink-subtle);
  line-height: 1;
}

/* Spans 09:00 to 11:00: the second line to the fourth. */
.slot-board__slot {
  position: absolute;
  inset-block: 33.333% 0;
  inset-inline: var(--label-col) 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: flex-start;
  gap: var(--space-1);
  padding: var(--space-4) var(--space-5);
  border-radius: var(--radius-2xl);
  border-inline-start: 6px solid var(--brand-press);
  background: var(--brand);
  color: var(--on-brand);
}

.slot-board__time {
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  font-variant-numeric: tabular-nums;
  opacity: 0.85;
}

.slot-board__title {
  font-size: var(--text-lg);
  font-weight: var(--weight-semibold);
}

/* ------------------------------------------------------------ live slot */

.slot-board__seats {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  margin-block-start: var(--space-2);
}

.seat-row {
  display: flex;
  gap: 4px;
}

/* An empty seat is an outline; a taken one fills and shows who took it. */
.seat {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  inline-size: 20px;
  block-size: 20px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.45);
  font-size: 10px;
  font-weight: var(--weight-bold);
  line-height: 1;
  color: transparent;
  transition: background-color var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out),
    color var(--dur-base) var(--ease-out);
}

.seat.is-taken {
  border-color: var(--surface);
  background: var(--surface);
  color: var(--brand-ink);
  animation: seat-in var(--dur-slow) var(--ease-spring);
}

@keyframes seat-in {
  from {
    transform: scale(0.4);
  }
}

.seat-count {
  min-inline-size: 3.25em;
  padding: 2px var(--space-2);
  border-radius: var(--radius-pill);
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
  text-align: center;
  background: rgba(255, 255, 255, 0.16);
  transition: background-color var(--dur-base) var(--ease-out),
    color var(--dur-base) var(--ease-out);
}

.seat-count.is-full {
  background: var(--accent);
  color: var(--on-brand);
  animation: seat-in var(--dur-slow) var(--ease-spring);
}

.slot-board__status {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin-block-start: var(--space-2);
  padding: 2px var(--space-3);
  border: 1.5px solid rgba(255, 255, 255, 0.55);
  border-radius: var(--radius-pill);
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  transition: background-color var(--dur-base) var(--ease-out),
    color var(--dur-base) var(--ease-out);
}

.slot-board__status.is-booked {
  border-color: var(--surface);
  background: var(--surface);
  color: var(--brand-ink);
  animation: seat-in var(--dur-slow) var(--ease-spring);
}

.slot-board__status :deep(svg) {
  inline-size: 13px;
  block-size: 13px;
  stroke-width: 3;
}

/*
 * The current time. The wrapper is as tall as the board, so translating it
 * by a percentage moves the line from the 08:00 rule to the 11:00 rule.
 */
.slot-board__now {
  position: absolute;
  inset: 0;
  inset-inline-start: calc(var(--label-col) - 6px);
  z-index: 1;
  pointer-events: none;
  animation: now-sweep 36s linear infinite;
}

.slot-board__now i {
  position: absolute;
  inset-inline: 0;
  inset-block-start: 0;
  block-size: 2px;
  background: var(--accent);
}

.slot-board__now i::before {
  content: "";
  position: absolute;
  inset-inline-start: 0;
  inset-block-start: -4px;
  inline-size: 10px;
  block-size: 10px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 4px rgba(255, 122, 69, 0.25);
}

@keyframes now-sweep {
  from {
    transform: translate3d(0, 12%, 0);
  }
  to {
    transform: translate3d(0, 96%, 0);
  }
}

/*
 * Pushed to the outer edge, leaving the slot's corner and its label clear
 * on the reading side. Leans towards the pointer, a few degrees at most.
 */
.slot-board__devices {
  --tilt-x: 0;
  --tilt-y: 0;
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  inline-size: 84%;
  margin-inline-start: auto;
  padding-block-start: var(--space-4);
  transform: perspective(1100px) rotateY(calc(var(--tilt-x) * 6deg))
    rotateX(calc(var(--tilt-y) * -5deg));
  transition: transform 600ms var(--ease-out);
}

.hero__device {
  inline-size: min(100%, 460px);
  block-size: auto;
}

/* --------------------------------------------------------------- desktop */

@media (min-width: 900px) {
  .hero {
    padding-block: var(--space-9) var(--section-gap);
  }

  .hero__inner {
    grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
    gap: var(--space-8);
  }

  .hero__visual {
    order: 2;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero__orbit i {
    animation: none;
  }

  .slot-board__now {
    animation: none;
    transform: translate3d(0, 45%, 0);
  }
}

/*
 * On small screens the copy and the form come first. The mockup is worth
 * showing, but not worth a whole viewport before the visitor reads anything.
 */
@media (max-width: 899px) {
  .hero__inner {
    gap: var(--space-6);
  }

  /* The ring sits behind the copy here; a moving dot would cross the text. */
  .hero__orbit i {
    display: none;
  }

  .hero__visual {
    order: 2;
  }

  .slot-board {
    --label-col: 2.75rem;
  }

  .hero__device {
    inline-size: min(100%, 320px);
  }
}
</style>
